import classNames from 'classnames';
import React, { forwardRef } from 'react';
import { ComponentSize } from '../types';
import Link from 'next/link';

export const DEFAULT_STYLES = 'rounded-full border-base-content/20 shadow-none';
export const PRIMARY_STYLES = 'btn-primary rounded-full text-white';
export const MUTATE_STYLES = 'btn-success rounded-full text-white';
export const MUTATE_COLORED_BG_STYLES = 'btn-neutral rounded-full text-white';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  size?: ComponentSize;
}

export const ButtonBase = forwardRef(function ButtonBase(
  props: ButtonProps,
  ref: React.Ref<HTMLButtonElement>
) {
  const { size = 'md' } = props;
  return (
    <button
      ref={ref}
      type="button"
      {...props}
      className={classNames(
        'btn whitespace-nowrap',
        {
          'btn-sm': size === 'sm',
          'btn-xs': size === 'xs',
          'btn-md': size === 'md',
        },
        props.className || {}
      )}
    >
      {props.children}
    </button>
  );
});

export const DefaultButton = forwardRef(function DefaultButton(
  props: ButtonProps,
  ref: React.Ref<HTMLButtonElement>
) {
  return (
    <ButtonBase
      ref={ref}
      {...props}
      className={classNames(DEFAULT_STYLES, props.className || {})}
      style={{ '--btn-color': 'var(--color-base-100)' } as React.CSSProperties}
    />
  );
});

export const Button = DefaultButton;

export const GhostButton = forwardRef(function GhostButton(
  props: ButtonProps,
  ref: React.Ref<HTMLButtonElement>
) {
  return (
    <ButtonBase
      ref={ref}
      {...props}
      className={classNames('btn-ghost rounded-full', props.className || {})}
    />
  );
});

export const NeutralButton = forwardRef(function NeutralButton(
  props: ButtonProps,
  ref: React.Ref<HTMLButtonElement>
) {
  return (
    <ButtonBase
      ref={ref}
      {...props}
      className={classNames('btn-neutral rounded-full', props.className || {})}
    />
  );
});

export const PrimaryButton = forwardRef(function PrimaryButton(
  props: ButtonProps,
  ref: React.Ref<HTMLButtonElement>
) {
  return (
    <ButtonBase
      ref={ref}
      {...props}
      className={classNames(PRIMARY_STYLES, props.className || {})}
    />
  );
});

export const MutateButton = forwardRef(function MutateButton(
  props: ButtonProps,
  ref: React.Ref<HTMLButtonElement>
) {
  return (
    <ButtonBase ref={ref} {...props} className={classNames(MUTATE_STYLES, props.className || {})} />
  );
});

export const MutateForSpecialBackgroundButton = forwardRef(function MutateButton(
  props: ButtonProps,
  ref: React.Ref<HTMLButtonElement>
) {
  return (
    <ButtonBase
      ref={ref}
      {...props}
      className={classNames(MUTATE_COLORED_BG_STYLES, props.className || {})}
    />
  );
});

const RoundButtonBase = forwardRef(function RoundButtonBase(
  props: ButtonProps,
  ref: React.Ref<HTMLButtonElement>
) {
  const { size = 'md' } = props;

  return (
    <ButtonBase
      ref={ref}
      {...props}
      className={classNames(
        'rounded-full p-0',
        { 'w-10': size === 'md', 'w-8': size === 'sm', 'w-6': size === 'xs' },
        props.className || {}
      )}
    />
  );
});

export const RoundDefaultButton = forwardRef(function RoundDefaultButton(
  props: ButtonProps,
  ref: React.Ref<HTMLButtonElement>
) {
  return (
    <RoundButtonBase
      ref={ref}
      {...props}
      className={classNames(DEFAULT_STYLES, props.className || {})}
      style={{ '--btn-color': 'var(--color-base-100)' } as React.CSSProperties}
    />
  );
});

export const RoundButton = RoundDefaultButton;

export const PrimaryRoundButton = forwardRef(function PrimaryRoundButton(
  props: ButtonProps,
  ref: React.Ref<HTMLButtonElement>
) {
  return (
    <RoundButtonBase
      ref={ref}
      {...props}
      className={classNames('btn-primary', props.className || {})}
    />
  );
});

export const NeutralRoundButton = forwardRef(function PrimaryRoundButton(
  props: ButtonProps,
  ref: React.Ref<HTMLButtonElement>
) {
  return (
    <RoundButtonBase
      ref={ref}
      {...props}
      className={classNames('btn-neutral', props.className || {})}
    />
  );
});

export const GhostRoundButton = forwardRef(function PrimaryRoundButton(
  props: ButtonProps,
  ref: React.Ref<HTMLButtonElement>
) {
  return (
    <RoundButtonBase
      ref={ref}
      {...props}
      className={classNames('btn-ghost', props.className || {})}
    />
  );
});

export const OutlineButton = function NeutralButton(
  props: ButtonProps,
  ref?: React.Ref<HTMLButtonElement>
) {
  return (
    <ButtonBase
      ref={ref}
      {...props}
      className={classNames(
        'btn-solid btn-xs rounded-full border-crns-black',
        '[--btn-color:theme(colors.crns-white)]',
        'px-4 py-[14px]',
        props.className || {}
      )}
    />
  );
};

export interface ButtonLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  children: React.ReactNode;
  size?: ComponentSize;
  disabled?: boolean;
}

export const ButtonLinkBase = forwardRef(function ButtonLink(
  props: ButtonLinkProps,
  ref: React.Ref<HTMLAnchorElement>
) {
  const { size = 'md' } = props;
  if (props.href === undefined) {
    return null;
  }

  const allClassNames = classNames(
    'btn',
    {
      'btn-sm text-xs': size === 'sm',
      'h-10 min-h-10': size === 'md',
      'btn-xs': size === 'xs',
      'btn-disabled': props.disabled,
    },
    props.className || {}
  );

  if (props.disabled) {
    return <span className={allClassNames}>{props.children}</span>;
  }

  return (
    <Link ref={ref} {...props} href={props.href} prefetch={false} className={allClassNames}>
      {props.children}
    </Link>
  );
});

export const DefaultButtonLink = forwardRef(function DefaultButtonLink(
  props: ButtonLinkProps,
  ref: React.Ref<HTMLAnchorElement>
) {
  return (
    <ButtonLinkBase
      ref={ref}
      {...props}
      className={classNames(DEFAULT_STYLES, props.className || {})}
      style={{ '--btn-color': 'var(--color-base-100)' } as React.CSSProperties}
    />
  );
});

export const NeutralButtonLink = forwardRef(function PrimaryButtonLink(
  props: ButtonLinkProps,
  ref: React.Ref<HTMLAnchorElement>
) {
  return (
    <ButtonLinkBase
      ref={ref}
      {...props}
      className={classNames('btn-neutral rounded-full', props.className || {})}
    />
  );
});

export const PrimaryButtonLink = forwardRef(function PrimaryButtonLink(
  props: ButtonLinkProps,
  ref: React.Ref<HTMLAnchorElement>
) {
  return (
    <ButtonLinkBase
      ref={ref}
      {...props}
      className={classNames(PRIMARY_STYLES, props.className || {})}
    />
  );
});

export const ButtonBorderedLink = forwardRef(function GhostButtonLink(
  props: ButtonLinkProps,
  ref: React.Ref<HTMLAnchorElement>
) {
  return (
    <ButtonLinkBase
      ref={ref}
      {...props}
      className={classNames(
        'btn-bordered border-crns-black rounded-full bg-white py-3',
        props.className || {}
      )}
    />
  );
});

export const DefaultRoundButtonLink = forwardRef(function RoundButtonLink(
  props: ButtonLinkProps,
  ref: React.Ref<HTMLAnchorElement>
) {
  const size = props.size || 'md';
  return (
    <ButtonLinkBase
      ref={ref}
      {...props}
      className={classNames(
        DEFAULT_STYLES,
        'p-0',
        { 'w-10': size === 'md', 'w-8': size === 'sm', 'w-6': size === 'xs' },
        props.className || {}
      )}
      style={{ '--btn-color': 'var(--color-base-100)' } as React.CSSProperties}
    />
  );
});

export const GhostRoundButtonLink = forwardRef(function RoundButtonLink(
  props: ButtonLinkProps,
  ref: React.Ref<HTMLAnchorElement>
) {
  const size = props.size || 'md';
  return (
    <ButtonLinkBase
      ref={ref}
      {...props}
      className={classNames(
        'btn-ghost rounded-full',
        'p-0',
        { 'w-10': size === 'md', 'w-8': size === 'sm', 'w-6': size === 'xs' },
        props.className || {}
      )}
    />
  );
});

export const ButtonLink = DefaultButtonLink;

export default DefaultButton;
